import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Alert from "@mui/material/Alert";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  UserScopeRequest,
  SaveProfessionalInformationRequest,
  ProfessionalInformationSaveRequest,
  DemographicQuestion,
  QuestionSaveRequest,
  QuestionTypeEnum,
  ResponseOfProfessionalInformationResponse,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import CredentialQuestionField from "./CredentialQuestionField";

type ProfessionalInformationAccordionProps = {
  userId: string;
  organizationId: string;
  setSnackbarMessage: (msg: SnackbarMessageProps) => void;
  onDataChanged: () => void;
};

type QuestionState = {
  userResponse: string;
  userResponseId: string;
  userResponseArray: string[];
  wasEdited: boolean;
};

const ProfessionalInformationAccordion = ({
  userId,
  organizationId,
  setSnackbarMessage,
  onDataChanged,
}: ProfessionalInformationAccordionProps) => {
  const { formatMessage: t } = useIntl();
  const { professionalInformationService } = useGlobalContext();

  const flattenQuestions = (sets: typeof questionSets) =>
    sets.flatMap((set) => set.questions ?? []);

  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<Record<string, QuestionState>>({});

  const result = useStrictQuery<ResponseOfProfessionalInformationResponse>(
    ["getProfessionalInformation", userId],
    () =>
      professionalInformationService.getProfessionalInformation(
        UserScopeRequest.fromJS({
          userId,
          organizationId,
        }),
      ),
    { enabled: expanded },
  );

  const questionSets = useMemo(() => result.data?.data?.questionSets ?? [], [
    result.data,
  ]);

  const resetFormState = useCallback(() => {
    const state: Record<string, QuestionState> = {};
    questionSets.forEach((set) =>
      set.questions?.forEach((q) => {
        state[q.id!] = {
          userResponse: q.response ?? "",
          userResponseId: q.responseId ?? "",
          userResponseArray: q.responseArray ?? [],
          wasEdited: false,
        };
      }),
    );
    setFormState(state);
  }, [questionSets]);

  useEffect(() => {
    if (questionSets.length === 0) return;
    const allQuestions = flattenQuestions(questionSets);
    setFormState((prev) => {
      const next = { ...prev };
      allQuestions.forEach((q) => {
        if (!next[q.id!]?.wasEdited) {
          next[q.id!] = {
            userResponse: q.response ?? "",
            userResponseId: q.responseId ?? "",
            userResponseArray: q.responseArray ?? [],
            wasEdited: false,
          };
        }
      });
      return next;
    });
  }, [questionSets]);

  const updateQuestion = (
    questionId: string,
    updates: Partial<QuestionState>,
  ) => {
    setFormState((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], ...updates, wasEdited: true },
    }));
  };

  const hasEdits = Object.values(formState).some((q) => q.wasEdited);

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const editedQuestions = flattenQuestions(questionSets)
        .filter((q) => formState[q.id!]?.wasEdited)
        .map((q) => {
          const state = formState[q.id!];
          return QuestionSaveRequest.fromJS({
            id: q.id,
            questionId: q.questionId,
            type: q.type,
            userResponse: state.userResponse,
            userResponseId: state.userResponseId,
            userResponseArray: state.userResponseArray,
            choices:
              q.choices?.map((c) => ({ id: c.id, value: c.value })) ?? [],
          });
        });

      await professionalInformationService.saveProfessionalInformation(
        SaveProfessionalInformationRequest.fromJS({
          userId,
          organizationId,
          professionalInformation: ProfessionalInformationSaveRequest.fromJS({
            questions: editedQuestions,
          }),
        }),
      );

      onDataChanged();
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "professionalInfoSaved" })),
      );
      setFormState((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach(
          (k) => (next[k] = { ...next[k], wasEdited: false }),
        );
        return next;
      });
    } catch (err: any) {
      setError(err?.data?.message ?? err?.message ?? "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (q: DemographicQuestion) => {
    const state = formState[q.id!];
    if (!state) return null;
    const label = q.value ?? "";
    const required = q.isRequired ?? false;

    switch (q.type) {
      case QuestionTypeEnum.MultiSelect:
      case QuestionTypeEnum.CheckboxList:
      case QuestionTypeEnum.CreatableMultiSelect: {
        const options = q.choices?.map((c) => c.value ?? "") ?? [];
        return (
          <Grid item xs={12} key={q.id}>
            <TextField
              variant="standard"
              label={label}
              required={required}
              fullWidth
              select
              SelectProps={{
                multiple: true,
                value: state.userResponseArray ?? [],
                onChange: (e) =>
                  updateQuestion(q.id!, {
                    userResponseArray: e.target.value as string[],
                  }),
                renderValue: (selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((val) => (
                      <Chip key={val} size="small" label={val} />
                    ))}
                  </Box>
                ),
              }}
            >
              {options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        );
      }

      case QuestionTypeEnum.TwoOptionsRadioButton:
        return (
          <Grid item xs={12} key={q.id}>
            <CredentialQuestionField
              label={label}
              type={q.type}
              value={state.userResponseArray?.[0] ?? ""}
              required={required}
              answerId={state.userResponseArray?.[0] ?? ""}
              choices={q.choices?.map((c) => ({ id: c.value, value: c.value }))}
              onChange={(val) =>
                updateQuestion(q.id!, { userResponseArray: val ? [val] : [] })
              }
            />
          </Grid>
        );

      case QuestionTypeEnum.SingleSelect:
      case QuestionTypeEnum.LookupQuestion:
      case QuestionTypeEnum.CreatableSingleSelect:
        return (
          <Grid item xs={12} key={q.id}>
            <CredentialQuestionField
              label={label}
              type={q.type}
              value={state.userResponse}
              required={required}
              answerId={
                q.type === QuestionTypeEnum.SingleSelect
                  ? state.userResponseId
                  : state.userResponse
              }
              choices={q.choices?.map((c) => ({ id: c.id, value: c.value }))}
              onChange={(val, aid) => {
                if (q.type === QuestionTypeEnum.SingleSelect) {
                  updateQuestion(q.id!, { userResponseId: aid ?? "" });
                } else {
                  updateQuestion(q.id!, { userResponse: aid ?? val });
                }
              }}
            />
          </Grid>
        );

      default:
        return (
          <Grid item xs={12} key={q.id}>
            <CredentialQuestionField
              label={label}
              type={q.type!}
              value={state.userResponse}
              required={required}
              validationPattern={q.validationRule?.value}
              validationMessage={q.validationRule?.message}
              onChange={(val) => updateQuestion(q.id!, { userResponse: val })}
            />
          </Grid>
        );
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>
          <T id="professionalInformation" />
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <Box width="100%">
            {result.isFetching && questionSets.length === 0 && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            {questionSets.map((set, setIdx) => (
              <Box key={set.id} mb={3}>
                <Grid container spacing={2}>
                  {set.questions?.map(renderQuestion)}
                </Grid>
                {setIdx < questionSets.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}

            {error && (
              <Box mt={2}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            {questionSets.length > 0 && (
              <Box
                mt={2}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
              >
                {result.isFetching && <CircularProgress size={20} />}
                <Button
                  onClick={resetFormState}
                  disabled={!hasEdits || submitting}
                >
                  <T id="cancelButton" />
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!hasEdits || submitting}
                  startIcon={
                    submitting ? <CircularProgress size={16} /> : undefined
                  }
                >
                  <T id="saveButton" />
                </Button>
              </Box>
            )}
          </Box>
        </form>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProfessionalInformationAccordion;
